import React, { FC, useCallback, useState, useEffect } from "react";
import styled from "styled-components";
import { CartItem } from "./CartItem";
import { UpsellItem } from "./UpsellItem";
import { ProgressBar } from "./item-children/ProgressBar";
import { CartSummary } from "./item-children/Summary";
import { Alert } from "../alert/Alert";
import { Button } from "../button/Button";
import { Divider } from "../divider/Divider";
import { Heading } from "../heading/Heading";
import { getErrors } from "@pepdirect/helpers/cart";
import { logCartLineItemQtyChangedEvent } from "@pepdirect/helpers/analyticsLogger";
import { convertFromCentsToDollars } from "../../helpers/currency";
import { CartItemUpdateDto } from "@pepdirect/shared/types";
import { CartFragment } from "../../graphql/generated";
import { CmsProductGtinWithSlug, UpsellProduct } from "./common/types";
import { Device } from "../../helpers/screenSizes";
import { colors } from "../../styles/variables";

const StyledCart = styled.section`
  padding: 40px 20px 60px;
  width: 100%;

  @media ${Device.notMobile} {
    padding: 40px 30px 80px;
    margin: auto;
    max-width: ${({ theme }) => (theme.cart?.isFullWidth ? "none" : "1125px")};
  }
`;

const StyledHeading = styled(Heading)`
  @media ${Device.mobile} {
    text-align: center;
  }
`;

const TotalInHeadingMobile = styled.span`
  @media ${Device.notMobile} {
    display: none;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 20px;

  @media ${Device.mobile} {
    flex-direction: column-reverse;
  }
`;

const CartItems = styled.div`
  width: calc(100% - 375px);
  margin-bottom: 20px;
  box-sizing: border-box;
  vertical-align: top;

  @media ${Device.mobile} {
    width: 100%;
  }

  @media ${Device.tablet} {
    width: calc(100% - 300px);
  }
`;

const StyledAlert = styled.div`
  padding-bottom: 20px;
`;

const SummaryDesktop = styled.div`
  border-radius: 3px;
  box-sizing: border-box;
  vertical-align: top;
`;

const SummaryMobile = styled.div`
  @media ${Device.mobile} {
    width: 100%;
    margin-top: 0;
    padding: 0;
  }
`;

const ButtonMobile = styled.div`
  @media ${Device.mobile} {
    margin-top: 20px;
  }

  @media ${Device.notMobile} {
    display: none;
  }
`;

const ButtonNotMobile = styled.div`
  margin-top: 20px;
  text-align: right;

  button {
    width: 320px;
    display: inline-block;

    @media ${Device.mobile} {
      display: none;
    }
  }
`;

const SummaryAndUpsellsDesktop = styled.div`
  padding-left: 20px;
  width: 375px;

  @media ${Device.tablet} {
    width: 300px;
  }

  @media ${Device.mobile} {
    display: none;
  }
`;

const SummaryAndUpsellsMobile = styled.div`
  margin-top: 40px;

  @media ${Device.notMobile} {
    display: none;
  }
`;

const FreeShippingContainer = styled.div`
  border: 1px solid ${colors.gray};
  border-radius: ${({ theme }) => theme.borderRadius || "4px"};
  margin-bottom: 15px;
`;

const FreeShippingThresholdHeader = styled.div`
  min-height: 40px;
  padding: 10px;
  font-weight: 700;
  text-align: center;
  background: ${({ theme }) =>
    theme.cart?.freeShipSection?.headingBackgroundColor || theme.color.primary};
  color: ${({ theme }) =>
    theme.cart?.freeShipSection?.headingColor || colors.white};
  margin-bottom: 20px;
`;

const InnerWrapper = styled.div`
  padding: 0 20px;
  font-size: 14px;
`;

const UpsellItemsContainer = styled.div`
  margin-bottom: 15px;
`;

const UpsellItemsHeading = styled.div`
  color: ${({ theme }) =>
    theme.cart?.upsellSection?.productTitleColor || theme.color.primary};
  font-weight: 700;
  font-family: font-secondary, Arial, sans-serif;
  padding-bottom: 10px;
  font-size: 16px;
`;

const UpsellItems = styled.div`
  padding: 20px;
  border: 1px solid ${colors.gray};
  border-radius: ${({ theme }) => theme.borderRadius || "4px"};
`;

const StyledDivider = styled(Divider)`
  margin: 20px 0;
`;

export interface CartProps {
  size: number | undefined;
  cart: CartFragment;
  blockedItems?: string[];
  currentUserId: string | null;
  maxCartQty: number;
  productsImgCdnEndpoint: string;
  minPurchaseAmt?: number;
  minPurchaseQty?: number;
  freeShippingThreshold?: number | null;
  updateCartItem: (_dto: CartItemUpdateDto) => Promise<void>;
  goToCheckout: () => void;
  itemImgCdn?: string;
  upsellItems?: UpsellProduct[];
  cmsProducts?: CmsProductGtinWithSlug[];
}

export const Cart: FC<CartProps> = ({
  size,
  cart,
  blockedItems,
  currentUserId,
  maxCartQty,
  productsImgCdnEndpoint,
  minPurchaseAmt = 0,
  minPurchaseQty = 0,
  freeShippingThreshold = 0,
  updateCartItem,
  goToCheckout,
  upsellItems,
  cmsProducts,
}) => {
  const [hasBlockedItems, setHasBlockedItems] = useState(false);
  const [headingText, setHeadingText] = useState("Your cart");

  const { shipping, subtotal, total } = cart;

  const minPurchaseAmtNotMet = subtotal < minPurchaseAmt;
  const minPurchaseQtyNotMet = size && size < minPurchaseQty;
  const errors = getErrors(cart, currentUserId);
  const isButtonDisabled =
    minPurchaseAmtNotMet ||
    minPurchaseQtyNotMet ||
    !!errors?.length ||
    hasBlockedItems;

  useEffect(() => {
    if (size) {
      setHeadingText(`Your cart (${size} ${size > 1 ? "items" : "item"})`);
    } else {
      setHeadingText("Your cart");
    }
  }, [size]);

  useEffect(() => {
    if (blockedItems) {
      setHasBlockedItems(
        cart.items?.some((cartItem) =>
          blockedItems.includes(cartItem.product.id)
        )
      );
    }
  }, [cart, blockedItems]);

  const getBlockedItem = useCallback(
    (id: string) => {
      if (blockedItems && blockedItems.includes(id)) {
        return {
          id,
          isZipError: true,
        };
      }
      return undefined;
    },
    [blockedItems]
  );

  const isUpsellInCart = useCallback(
    (productId: string) => {
      return cart?.items?.some((cartItem) => cartItem.product.id === productId);
    },
    [cart]
  );

  const renderSummary = () => (
    <CartSummary
      cart={cart}
      subtotal={subtotal}
      total={total}
      shippingFee={shipping}
      isButtonDisabled={isButtonDisabled}
      goToCheckout={goToCheckout}
    />
  );

  const renderProgressBar = () =>
    !!freeShippingThreshold && (
      <FreeShippingContainer>
        <FreeShippingThresholdHeader>
          {`Free Shipping on all orders over ${convertFromCentsToDollars(
            freeShippingThreshold
          )}`}
        </FreeShippingThresholdHeader>
        <InnerWrapper>
          <ProgressBar
            subtotal={subtotal}
            freeShippingThreshold={freeShippingThreshold}
          />
        </InnerWrapper>
      </FreeShippingContainer>
    );

  const renderUpsellItems = () =>
    !!upsellItems?.length && (
      <UpsellItemsContainer>
        <UpsellItemsHeading>Recommended for You</UpsellItemsHeading>
        <UpsellItems>
          {upsellItems.map((upsellItem, index) => (
            <div key={`upsell-item-${index}`}>
              {!!index && <StyledDivider />}
              <UpsellItem
                key={upsellItem.gtin}
                cmsProducts={cmsProducts}
                blockedItems={blockedItems}
                currentUserId={currentUserId}
                product={upsellItem}
                index={index}
                itemImgCdn={productsImgCdnEndpoint}
                isInCart={isUpsellInCart(upsellItem._id)}
                updateCartItem={updateCartItem}
              />
            </div>
          ))}
        </UpsellItems>
      </UpsellItemsContainer>
    );

  return (
    <StyledCart>
      <StyledHeading level="h1" size="l">
        {headingText}
        <TotalInHeadingMobile data-testid="total-in-heading-mobile">
          {" "}
          {convertFromCentsToDollars(total)}
        </TotalInHeadingMobile>
      </StyledHeading>

      <ButtonMobile data-testid="cart-button-mobile">
        <Button
          type="secondary"
          fullWidth
          onClick={goToCheckout}
          disabled={isButtonDisabled}
        >
          Proceed To Checkout
        </Button>
      </ButtonMobile>

      <Content>
        <CartItems>
          {errors.includes("lowStock") && (
            <StyledAlert data-testid="cart-alert-low-stock">
              <Alert type="error">
                There are items in your cart where the quantity selected is more
                than we have in stock. Please adjust or remove these items to
                continue to checkout.
              </Alert>
            </StyledAlert>
          )}

          {errors.includes("noStock") && (
            <StyledAlert data-testid="cart-alert-no-stock">
              <Alert type="error">
                There are items in your cart that are <u>sold out</u>. Please
                remove them to continue to checkout.
              </Alert>
            </StyledAlert>
          )}

          {minPurchaseAmtNotMet && (
            <StyledAlert data-testid="cart-alert-info">
              <Alert>
                {`Free shipping on all orders ($${
                  minPurchaseAmt / 100
                } minimum purchase)`}
              </Alert>
            </StyledAlert>
          )}

          {minPurchaseQtyNotMet && (
            <StyledAlert data-testid="cart-alert-min-purchase-qty">
              <Alert>
                {`All Orders must meet the minimum order quantity of ${minPurchaseQty} items before checking out.`}
              </Alert>
            </StyledAlert>
          )}

          <Divider />

          {cart?.items?.map((cartItem, index) => {
            return (
              <CartItem
                {...cartItem}
                cart={cart}
                maxCartQty={maxCartQty}
                itemImgSrc={cartItem.product.imgUrl || ""}
                currentUserId={currentUserId}
                blockedItem={getBlockedItem(cartItem.product.id)}
                updateCartItem={updateCartItem}
                logCartLineItemQtyChangedEvent={logCartLineItemQtyChangedEvent}
                key={`cart-item-${index}`}
                itemImgCdn={productsImgCdnEndpoint}
                cmsProducts={cmsProducts}
              />
            );
          })}

          <SummaryAndUpsellsMobile>
            {renderProgressBar()}
            {renderUpsellItems()}
            <SummaryMobile>{renderSummary()}</SummaryMobile>
          </SummaryAndUpsellsMobile>

          {!!cart && cart.items.length > 1 && (
            <ButtonNotMobile data-testid="cart-button-not-mobile">
              <Button
                type="secondary"
                fullWidth
                onClick={goToCheckout}
                disabled={isButtonDisabled}
              >
                Proceed To Checkout
              </Button>
            </ButtonNotMobile>
          )}
        </CartItems>

        <SummaryAndUpsellsDesktop>
          {renderProgressBar()}
          {renderUpsellItems()}
          <SummaryDesktop>{renderSummary()}</SummaryDesktop>
        </SummaryAndUpsellsDesktop>
      </Content>
    </StyledCart>
  );
};
